import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Calculator, Plus, Trash2, DollarSign, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { calculatePayment } from '../../utils/calculations';
import PaywallGuard from '../PaywallGuard';
import HELOCAnalysis from '../HELOCAnalysis';
import AdvancedHELOC from '../AdvancedHELOC';
import ExportReports from '../ExportReports';

const StrategiesTab = ({ mortgage, scenarios, addScenario, deleteScenario, offers, user }) => {
  const [showModal, setShowModal] = useState(false);
  const [helocAmount, setHelocAmount] = useState('');
  const [helocRate, setHelocRate] = useState('7.5');
  const [helocTerm, setHelocTerm] = useState('10');
  const [scenarioLimit, setScenarioLimit] = useState(null);
  
  const [newScenario, setNewScenario] = useState({
    name: '',
    amount: '',
    frequency: 'monthly',
    start_date: '',
    end_date: '',
    description: ''
  });
  
  useEffect(() => {
    loadScenarioLimit();
  }, [scenarios]);
  
  const loadScenarioLimit = async () => {
    try {
      const response = await api.get(`/api/scenarios/limit`);
      setScenarioLimit(response.data);
    } catch (error) {
      console.error('Error loading scenario limit:', error);
    }
  };
  
  const calculateHELOCPayment = () => {
    if (!helocAmount || !helocRate || !helocTerm) return null;
    return calculatePayment(parseFloat(helocAmount), parseFloat(helocRate), parseInt(helocTerm), 'monthly');
  };
  
  const helocPayment = calculateHELOCPayment();
  
  const handleAddScenario = async () => {
    if (!newScenario.name || !newScenario.amount) {
      return;
    }
    
    // Check limit before trying to add
    if (scenarioLimit && !scenarioLimit.can_add) {
      toast.error(`You've reached the ${scenarioLimit.limit} scenario limit for your plan. Upgrade to add more.`);
      return;
    }
    
    const success = await addScenario({
      ...newScenario,
      amount: parseFloat(newScenario.amount)
    });
    
    if (success) {
      setNewScenario({
        name: '',
        amount: '',
        frequency: 'monthly',
        start_date: '',
        end_date: '',
        description: ''
      });
      setShowModal(false);
      loadScenarioLimit(); // Refresh limit info
    }
  };
  
  const equity = mortgage.home_value - mortgage.current_balance;
  const maxHELOC = equity * 0.8;
  
  return (
    <div data-testid="strategies-tab" className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-semibold">HELOC & Prepayment Strategies</h2>
        <p className="text-muted-foreground text-sm mt-1">Optimize your mortgage with smart strategies</p>
      </div>
      
      {/* HELOC Analysis */}
      {mortgage.current_balance && mortgage.home_value && (
        <HELOCAnalysis mortgage={mortgage} />
      )}
      
      {/* HELOC Calculator */}
      <PaywallGuard feature="heloc_calculator" user={user} fallbackMessage="HELOC calculator is available for Pro subscribers only.">
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-heading font-semibold flex items-center gap-2 mb-4">
            <Calculator className="h-5 w-5 text-primary" />
            HELOC Calculator
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">HELOC Amount (CAD $)</label>
                <input
                  type="number"
                  value={helocAmount}
                  onChange={(e) => setHelocAmount(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="50000"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Max available: ${maxHELOC.toLocaleString('en-CA', {maximumFractionDigits: 0})}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1.5">Interest Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={helocRate}
                  onChange={(e) => setHelocRate(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="7.5"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1.5">Repayment Period (Years)</label>
                <input
                  type="number"
                  value={helocTerm}
                  onChange={(e) => setHelocTerm(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="10"
                />
              </div>
            </div>
            
            <div className="bg-primary/5 rounded-lg p-4 flex flex-col justify-center">
              <h4 className="text-sm font-semibold mb-4">Payment Estimate</h4>
              {helocPayment ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Monthly Payment</p>
                    <p className="text-3xl font-bold font-mono">${helocPayment.amount.toLocaleString('en-CA', {maximumFractionDigits: 2})}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Interest</p>
                    <p className="text-xl font-bold font-mono">
                      ${((helocPayment.amount * parseInt(helocTerm) * 12) - parseFloat(helocAmount)).toLocaleString('en-CA', {maximumFractionDigits: 2})}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">Enter amounts to see estimates</p>
              )}
            </div>
          </div>
          
          <div className="mt-4 bg-background rounded-lg p-4">
            <h4 className="text-sm font-semibold mb-2">HELOC Strategies</h4>
            <ul className="space-y-1.5 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Use HELOC for debt consolidation at lower rates</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Access funds for home renovations to increase property value</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Create emergency fund without liquidating investments</span>
              </li>
            </ul>
          </div>
        </div>
      </PaywallGuard>
      
      {/* Prepayment Scenarios */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Prepayment Scenarios
            </h3>
            {scenarioLimit && (
              <p className="text-xs text-muted-foreground mt-1">
                {scenarioLimit.limit === -1 
                  ? 'Unlimited scenarios (Pro)' 
                  : `${scenarioLimit.current_count}/${scenarioLimit.limit} scenarios used`
                }
              </p>
            )}
          </div>
          <button
            onClick={() => setShowModal(true)}
            data-testid="add-scenario-btn"
            disabled={scenarioLimit && !scenarioLimit.can_add}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            Add Scenario
          </button>
        </div>
        
        {scenarioLimit && !scenarioLimit.can_add && (
          <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-yellow-400" />
            <span className="text-sm text-yellow-400">
              You've reached the scenario limit for your plan. 
              <a href="/dashboard?tab=settings" className="underline ml-1">Upgrade</a> for more.
            </span>
          </div>
        )}
        
        {scenarios.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No prepayment scenarios yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create scenarios to model different prepayment strategies</p>
          </div>
        ) : (
          <div className="space-y-3">
            {scenarios.map(scenario => (
              <div key={scenario.scenario_id} className="bg-background rounded-lg p-4 flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">{scenario.name}</h4>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>${scenario.amount.toLocaleString('en-CA')} {scenario.frequency}</p>
                    {scenario.start_date && <p>Start: {scenario.start_date}</p>}
                    {scenario.description && <p>{scenario.description}</p>}
                  </div>
                </div>
                <button
                  onClick={() => deleteScenario(scenario.scenario_id)}
                  className="text-red-400 hover:text-red-300 p-2"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Add Scenario Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-heading font-semibold mb-4">Add Prepayment Scenario</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Scenario Name</label>
                <input
                  type="text"
                  value={newScenario.name}
                  onChange={(e) => setNewScenario({...newScenario, name: e.target.value})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Aggressive Prepayment"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Amount (CAD $)</label>
                  <input
                    type="number"
                    value={newScenario.amount}
                    onChange={(e) => setNewScenario({...newScenario, amount: e.target.value})}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1.5">Frequency</label>
                  <select
                    value={newScenario.frequency}
                    onChange={(e) => setNewScenario({...newScenario, frequency: e.target.value})}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="weekly">Weekly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1.5">Description (Optional)</label>
                <textarea
                  value={newScenario.description}
                  onChange={(e) => setNewScenario({...newScenario, description: e.target.value})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows="3"
                  placeholder="Describe your prepayment strategy..."
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddScenario}
                data-testid="save-scenario-btn"
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Add Scenario
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Advanced HELOC Calculator - Pro Feature */}
      <AdvancedHELOC user={user} mortgage={mortgage} />
      
      {/* Export Reports - Pro Feature */}
      <ExportReports user={user} />
    </div>
  );
};

export default StrategiesTab;